import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Nav,
  Button,
  Modal,
  Card,
  Spinner,
  InputGroup,
  Form,
} from 'react-bootstrap';
import {
  Outlet,
  Link,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import {
  FaBars,
  FaTachometerAlt,
  FaBuilding,
  FaUsers,
  FaUserCircle,
  FaTruck,
  FaBoxOpen,
  FaUserTie,
  FaChartBar,
  FaSignOutAlt,
  FaMapMarkerAlt,
  FaMapPin,
  FaCar,
  FaShareAlt,
  FaLink,
  FaExternalLinkAlt,
  FaWhatsapp,
  FaEnvelope,
  FaQrcode,
  FaList,
  FaStore,
  FaChevronDown,
  FaChevronRight,
  FaDollarSign,
  FaCreditCard,
} from 'react-icons/fa';
import './AdminLayout.css';
import axios from 'axios';
import BASE_URL from '../config';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const createIcon = (color, icon = null) => {
  return L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; color: white; font-size: 14px; font-weight: bold;">${
      icon || ''
    }</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
};

const pickupIcon = createIcon('#28a745', '📍');
const dropIcon = createIcon('#dc3545', '🏁');
const driverIcon = createIcon('#007bff', '🚗');

const FitBounds = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds && bounds.length > 0) {
      const leafletBounds = L.latLngBounds(bounds);
      map.fitBounds(leafletBounds, { padding: [50, 50], maxZoom: 16 });
    }
  }, [bounds, map]);

  return null;
};

const authAxios = axios.create({
  baseURL: BASE_URL,
});

authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const geocodeLatLng = async (lat, lng) => {
  try {
    const res = await axios.get(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=en`
    );
    return res.data.display_name || 'Address not found';
  } catch (err) {
    console.error('Geocoding failed:', err);
    return 'Geocoding failed';
  }
};

const AdminLayout = () => {
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [currentDriverLocation, setCurrentDriverLocation] = useState(null);
  const [driverAddress, setDriverAddress] = useState('');
  const [trackingInterval, setTrackingInterval] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [reportsSubmenuOpen, setReportsSubmenuOpen] = useState(false);

  const [messageModal, setMessageModal] = useState({
    show: false,
    title: '',
    body: '',
  });

  const showMessageModal = (title, body) => {
    setMessageModal({ show: true, title, body });
  };

  const handleCloseMessageModal = () => {
    setMessageModal({ show: false, title: '', body: '' });
  };

  const handleShareTracking = () => {
    if (!trackingOrder) {
      showMessageModal('Error', 'No tracking data available to share.');
      return;
    }

    const baseUrl = window.location.origin;
    const trackingUrl = `${baseUrl}/track-order/${trackingOrder.id}${
      trackingOrder.driver_id ? `?driverId=${trackingOrder.driver_id}` : ''
    }`;

    const message =
      `Tracking Order #${trackingOrder.order_number}\n` +
      `Driver: ${trackingOrder.driver_name || 'Not assigned'}\n` +
      `Current Location: ${driverAddress || 'Not available'}\n` +
      `Coordinates: ${
        currentDriverLocation
          ? `${currentDriverLocation.lat}, ${currentDriverLocation.lng}`
          : 'Not available'
      }\n` +
      `Last Updated: ${
        currentDriverLocation
          ? new Date(currentDriverLocation.timestamp).toLocaleString()
          : 'N/A'
      }\n\n` +
      `Live Tracking Link: ${trackingUrl}`;

    setShareUrl(trackingUrl);
    setShareMessage(message);
    setQrCodeUrl(
      `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
        trackingUrl
      )}`
    );
    setShowShareModal(true);
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        showMessageModal('Success', 'Link copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        showMessageModal('Error', 'Failed to copy link to clipboard.');
      });
  };

  const handleWebShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: `Order Tracking - #${trackingOrder.order_number}`,
          text: shareMessage,
          url: shareUrl,
        })
        .then(() => console.log('Successful share'))
        .catch((error) => console.log('Error sharing', error));
    } else {
      showMessageModal(
        'Info',
        'Web Share API not supported on this device. Please copy the link to share manually.'
      );
    }
  };

  const handleOpenTrackingLink = () => {
    window.open(shareUrl, '_blank');
  };

  const defaultCenter = [10.5276, 76.2144];
  const mapContainerStyle = {
    height: '350px',
    width: '100%',
  };

  const calculateMapBounds = () => {
    if (!trackingOrder) return defaultCenter;

    const points = [];

    if (trackingOrder.pickup_lat && trackingOrder.pickup_lng) {
      points.push([
        parseFloat(trackingOrder.pickup_lat),
        parseFloat(trackingOrder.pickup_lng),
      ]);
    }

    if (trackingOrder.delivery_lat && trackingOrder.delivery_lng) {
      points.push([
        parseFloat(trackingOrder.delivery_lat),
        parseFloat(trackingOrder.delivery_lng),
      ]);
    }

    if (currentDriverLocation) {
      points.push([currentDriverLocation.lat, currentDriverLocation.lng]);
    }

    if (points.length > 0) {
      const avgLat =
        points.reduce((sum, point) => sum + point[0], 0) / points.length;
      const avgLng =
        points.reduce((sum, point) => sum + point[1], 0) / points.length;
      return [avgLat, avgLng];
    }

    return defaultCenter;
  };

  const getRouteCoordinates = () => {
    if (!trackingOrder) return [];

    const route = [];

    if (trackingOrder.pickup_lat && trackingOrder.pickup_lng) {
      route.push([
        parseFloat(trackingOrder.pickup_lat),
        parseFloat(trackingOrder.pickup_lng),
      ]);
    }

    if (trackingOrder.delivery_lat && trackingOrder.delivery_lng) {
      route.push([
        parseFloat(trackingOrder.delivery_lat),
        parseFloat(trackingOrder.delivery_lng),
      ]);
    }

    return route;
  };

  const getMarkerPositions = () => {
    const positions = [];

    if (trackingOrder?.pickup_lat && trackingOrder?.pickup_lng) {
      positions.push([
        parseFloat(trackingOrder.pickup_lat),
        parseFloat(trackingOrder.pickup_lng),
      ]);
    }

    if (trackingOrder?.delivery_lat && trackingOrder?.delivery_lng) {
      positions.push([
        parseFloat(trackingOrder.delivery_lat),
        parseFloat(trackingOrder.delivery_lng),
      ]);
    }

    if (currentDriverLocation) {
      positions.push([currentDriverLocation.lat, currentDriverLocation.lng]);
    }

    return positions;
  };

  const fetchDriverLocation = async (driverId) => {
    try {
      const res = await authAxios.get(`/drivers/${driverId}/location`);
      const driverLocation = res.data?.data;

      if (driverLocation && driverLocation.lat && driverLocation.lng) {
        const lat = parseFloat(driverLocation.lat);
        const lng = parseFloat(driverLocation.lng);
        setCurrentDriverLocation({
          lat,
          lng,
          timestamp: driverLocation.timestamp,
        });

        const address = await geocodeLatLng(lat, lng);
        setDriverAddress(address);

        console.log('Driver location found:', driverLocation);
      } else {
        console.warn('No location data found for driver:', driverId);
        setCurrentDriverLocation(null);
        setDriverAddress('');
      }
    } catch (err) {
      console.error('Error fetching driver location:', err);
      setCurrentDriverLocation(null);
      setDriverAddress('');
    }
  };

  const handleTrackDriver = (order) => {
    setTrackingOrder(order);
    setShowTrackModal(true);
    fetchDriverLocation(order.driver_id);

    const intervalId = setInterval(() => {
      fetchDriverLocation(order.driver_id);
    }, 30000);
    setTrackingInterval(intervalId);
  };

  const handleCloseTrackModal = () => {
    setShowTrackModal(false);
    setTrackingOrder(null);
    setCurrentDriverLocation(null);
    setDriverAddress('');
    if (trackingInterval) {
      clearInterval(trackingInterval);
      setTrackingInterval(null);
    }
  };
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-expand reports submenu when on reports pages
  useEffect(() => {
    if (location.pathname.includes('/reports')) {
      setReportsSubmenuOpen(true);
    }
  }, [location.pathname]);

  const [sidebarVisible, setSidebarVisible] = useState(false);
  const token = localStorage.getItem('authToken');

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/');
  };

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  useEffect(() => {
    const handleResize = () => {
      // Keep it closed by default, even on large screens
      if (window.innerWidth < 992) {
        setSidebarVisible(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Always close sidebar when navigating to a new route
    setSidebarVisible(false);
  }, [location.pathname]);

  if (!token) {
    return <Navigate to='/' replace />;
  }

  return (
    <Container fluid className='p-0'>
      <div className='d-flex align-items-center bg-light p-2 shadow-sm'>
        <Button variant='light' onClick={toggleSidebar}>
          <FaBars />
        </Button>
        <img
          src='/sparemate_logo.png'
          alt='Sparemate'
          className='me-2'
          style={{ width: '30px', height: '30px' }}
        />
        <h4 className='m-0'>Sparemate</h4>
      </div>

      <div className='d-flex'>
        <div className={`sidebar-container ${sidebarVisible ? 'active' : ''}`}>
          <Nav
            className='flex-column pt-3'
            style={{
              maxHeight:
                'row justify-content-between align-items-center mt-3 mb-3',
            }}
          >
            <Nav.Link
              as={Link}
              to='/Dashboard'
              className={`nav-item ${
                location.pathname === '/Dashboard' ? 'active' : ''
              }`}
            >
              <FaTachometerAlt className='me-2' /> <span>Dashboard</span>
            </Nav.Link>
            <Nav.Link
              as={Link}
              to='customers'
              className={`nav-item ${
                location.pathname.includes('/customers') &&
                !location.pathname.includes('/reports')
                  ? 'active'
                  : ''
              }`}
            >
              <FaUsers className='me-2' /> <span>Customers</span>
            </Nav.Link>
            <Nav.Link
              as={Link}
              to='orders'
              className={`nav-item ${
                location.pathname.includes('/orders') &&
                !location.pathname.includes('/reports')
                  ? 'active'
                  : ''
              }`}
            >
              <FaBoxOpen className='me-2' /> <span>Orders</span>
            </Nav.Link>
            <Nav.Link
              as={Link}
              to='pay-and-collect'
              className={`nav-item ${
                location.pathname.includes('/pay-and-collect')
                  ? 'active'
                  : ''
              }`}
            >
              <FaDollarSign className='me-2' /> <span>Payments & Collections</span>
            </Nav.Link>
            <Nav.Link
              as={Link}
              to='drivers'
              className={`nav-item ${
                location.pathname.includes('/drivers') &&
                !location.pathname.includes('/reports')
                  ? 'active'
                  : ''
              }`}
            >
              <FaTruck className='me-2' /> <span>Drivers</span>
            </Nav.Link>
            <Nav.Link
              as={Link}
              to='vendors'
              className={`nav-item ${
                location.pathname.includes('/vendors') &&
                !location.pathname.includes('/reports')
                  ? 'active'
                  : ''
              }`}
            >
              <FaUserTie className='me-2' /> <span>Vendors</span>
            </Nav.Link>
            <Nav.Link
              as={Link}
              to='branches'
              className={`nav-item ${
                location.pathname.includes('/branches') &&
                !location.pathname.includes('/reports')
                  ? 'active'
                  : ''
              }`}
            >
              <FaBuilding className='me-2' /> <span>Branches</span>
            </Nav.Link>
            <Nav.Link
              as={Link}
              to='users'
              className={`nav-item ${
                location.pathname.includes('/users') &&
                !location.pathname.includes('/reports')
                  ? 'active'
                  : ''
              }`}
            >
              <FaUserCircle className='me-2' /> <span>Users</span>
            </Nav.Link>
            {/* Reports with Submenu */}
            <div>
              <div
                className='nav-item'
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 16px',
                }}
                onClick={() => setReportsSubmenuOpen(!reportsSubmenuOpen)}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FaChartBar className='me-2' /> <span>Reports</span>
                </div>
                {reportsSubmenuOpen ? (
                  <FaChevronDown size={12} />
                ) : (
                  <FaChevronRight size={12} />
                )}
              </div>
              {reportsSubmenuOpen && (
                <div className='submenu-container'>
                  <Nav.Link
                    as={Link}
                    to='reports/all-orders'
                    className={`submenu-item ${
                      location.pathname === '/Dashboard/reports' ||
                      location.pathname === '/Dashboard/reports/' ||
                      location.pathname === '/Dashboard/reports/all-orders'
                        ? 'active'
                        : ''
                    }`}
                  >
                    <FaList className='me-2' /> <span>All Orders</span>
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to='reports/drivers'
                    className={`submenu-item ${
                      location.pathname.includes('/reports/drivers')
                        ? 'active'
                        : ''
                    }`}
                  >
                    <FaTruck className='me-2' /> <span>Drivers</span>
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to='reports/vendors'
                    className={`submenu-item ${
                      location.pathname.includes('/reports/vendors')
                        ? 'active'
                        : ''
                    }`}
                  >
                    <FaStore className='me-2' /> <span>Vendors</span>
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to='reports/customers'
                    className={`submenu-item ${
                      location.pathname.includes('/reports/customers')
                        ? 'active'
                        : ''
                    }`}
                  >
                    <FaUsers className='me-2' /> <span>Customers</span>
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to='reports/payments'
                    className={`submenu-item ${
                      location.pathname.includes('/reports/payments')
                        ? 'active'
                        : ''
                    }`}
                  >
                    <FaCreditCard className='me-2' /> <span>Payments</span>
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to='reports/collections'
                    className={`submenu-item ${
                      location.pathname.includes('/reports/collections')
                        ? 'active'
                        : ''
                    }`}
                  >
                    <FaBoxOpen className='me-2' /> <span>Collections</span>
                  </Nav.Link>
                </div>
              )}
            </div>
            <Nav.Link
              onClick={handleLogout}
              className='nav-item logout mt-auto'
            >
              <FaSignOutAlt className='me-2' /> <span>Logout</span>
            </Nav.Link>
          </Nav>
        </div>

        <div className='content-container p-4'>
          <Outlet
            context={{
              handleTrackDriver,
              showTrackModal,
              handleCloseTrackModal,
              trackingOrder,
              currentDriverLocation,
              driverAddress,
              setTrackingInterval,
            }}
          />
        </div>
      </div>

      <Modal show={showTrackModal} onHide={handleCloseTrackModal} size='lg'>
        <Modal.Header closeButton className='bg-primary text-white'>
          <Modal.Title>
            <FaMapMarkerAlt className='me-2' /> Track Order #
            {trackingOrder?.order_number}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {trackingOrder ? (
            <div>
              <Row className='mb-3'>
                <Col md={4}>
                  <Card className='h-100 border-success'>
                    <Card.Body>
                      <Card.Title className='text-success'>
                        <FaMapPin className='me-2' /> Pickup
                      </Card.Title>
                      <Card.Text>
                        <small>{trackingOrder.pickup_address || 'N/A'}</small>
                        <br />
                        <small className='text-muted'>
                          {trackingOrder.pickup_lat && trackingOrder.pickup_lng
                            ? `${parseFloat(trackingOrder.pickup_lat).toFixed(
                                4
                              )}, ${parseFloat(
                                trackingOrder.pickup_lng
                              ).toFixed(4)}`
                            : 'N/A'}
                        </small>
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className='h-100 border-danger'>
                    <Card.Body>
                      <Card.Title className='text-danger'>
                        <FaMapPin className='me-2' /> Drop
                      </Card.Title>
                      <Card.Text>
                        <small>{trackingOrder.drop_address || 'N/A'}</small>
                        <br />
                        <small className='text-muted'>
                          {trackingOrder.delivery_lat &&
                          trackingOrder.delivery_lng
                            ? `${parseFloat(trackingOrder.delivery_lat).toFixed(
                                4
                              )}, ${parseFloat(
                                trackingOrder.delivery_lng
                              ).toFixed(4)}`
                            : 'N/A'}
                        </small>
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className='h-100 border-primary'>
                    <Card.Body>
                      <Card.Title className='text-primary'>
                        <FaCar className='me-2' /> Driver
                      </Card.Title>
                      <Card.Text>
                        <small>{trackingOrder.driver_name}</small>
                        <br />
                        <small className='text-muted'>
                          {currentDriverLocation
                            ? `${currentDriverLocation.lat.toFixed(
                                4
                              )}, ${currentDriverLocation.lng.toFixed(4)}`
                            : 'Fetching...'}
                        </small>
                        {driverAddress && (
                          <>
                            <br />
                            <small className='text-muted'>
                              {driverAddress}
                            </small>
                          </>
                        )}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <div style={mapContainerStyle}>
                <MapContainer
                  center={calculateMapBounds()}
                  zoom={13}
                  style={mapContainerStyle}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                    language='en'
                    detectRetina={true}
                  />

                  {trackingOrder.pickup_lat && trackingOrder.pickup_lng && (
                    <Marker
                      position={[
                        parseFloat(trackingOrder.pickup_lat),
                        parseFloat(trackingOrder.pickup_lng),
                      ]}
                      icon={pickupIcon}
                    >
                      <Popup>
                        <strong>Pickup</strong>
                        <br />
                        <small>{trackingOrder.pickup_address}</small>
                      </Popup>
                    </Marker>
                  )}

                  {trackingOrder.delivery_lat && trackingOrder.delivery_lng && (
                    <Marker
                      position={[
                        parseFloat(trackingOrder.delivery_lat),
                        parseFloat(trackingOrder.delivery_lng),
                      ]}
                      icon={dropIcon}
                    >
                      <Popup>
                        <strong>Drop</strong>
                        <br />
                        <small>{trackingOrder.delivery_address}</small>
                      </Popup>
                    </Marker>
                  )}

                  {currentDriverLocation && (
                    <Marker
                      position={[
                        currentDriverLocation.lat,
                        currentDriverLocation.lng,
                      ]}
                      icon={driverIcon}
                    >
                      <Popup>
                        <strong>Driver Location</strong>
                        <br />
                        <small>
                          {driverAddress || 'Address not available'}
                        </small>
                        <br />
                        <small>
                          Last updated:{' '}
                          {new Date(
                            currentDriverLocation.timestamp
                          ).toLocaleString()}
                        </small>
                      </Popup>
                    </Marker>
                  )}

                  <Polyline
                    positions={getRouteCoordinates()}
                    color={'#007bff'}
                    weight={4}
                    opacity={0.7}
                  />

                  <FitBounds bounds={getMarkerPositions()} />
                </MapContainer>
              </div>

              <div className='mt-3 d-flex justify-content-center'>
                <div className='d-flex align-items-center me-4'>
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#28a745',
                      borderRadius: '50%',
                      marginRight: '8px',
                    }}
                  ></div>
                  <span>Pickup</span>
                </div>
                <div className='d-flex align-items-center me-4'>
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#dc3545',
                      borderRadius: '50%',
                      marginRight: '8px',
                    }}
                  ></div>
                  <span>Drop</span>
                </div>
                <div className='d-flex align-items-center'>
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#007bff',
                      borderRadius: '50%',
                      marginRight: '8px',
                    }}
                  ></div>
                  <span>Driver</span>
                </div>
              </div>
            </div>
          ) : (
            <div className='text-center my-4'>
              <Spinner animation='border' />
              <div>Loading tracking data...</div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='success' onClick={handleShareTracking}>
            <FaShareAlt className='me-2' /> Share Tracking
          </Button>
          <Button variant='secondary' onClick={handleCloseTrackModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        size='lg'
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaShareAlt className='me-2' /> Share Order Tracking
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={8}>
              <Form.Group className='mb-3'>
                <Form.Label>Tracking Link</Form.Label>
                <InputGroup>
                  <Form.Control type='text' value={shareUrl} readOnly />
                  <Button
                    variant='outline-secondary'
                    onClick={handleCopyToClipboard}
                  >
                    <FaLink />
                  </Button>
                  <Button
                    variant='outline-primary'
                    onClick={handleOpenTrackingLink}
                  >
                    <FaExternalLinkAlt />
                  </Button>
                </InputGroup>
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label>Share Message</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={4}
                  value={shareMessage}
                  readOnly
                />
              </Form.Group>

              <div className='d-grid gap-2'>
                <Button variant='primary' onClick={handleWebShare}>
                  <FaShareAlt className='me-2' /> Share via Device Options
                </Button>

                <Button
                  variant='outline-success'
                  href={`https://wa.me/?text=${encodeURIComponent(
                    shareMessage
                  )}`}
                  target='_blank'
                >
                  <FaWhatsapp className='me-2' /> Share on WhatsApp
                </Button>

                <Button
                  variant='outline-info'
                  href={`mailto:?subject=Order Tracking&body=${encodeURIComponent(
                    shareMessage
                  )}`}
                  target='_blank'
                >
                  <FaEnvelope className='me-2' /> Share via Email
                </Button>
              </div>
            </Col>
            <Col md={4} className='text-center'>
              <div className='mb-3'>
                <h5>QR Code</h5>
                <p>Scan to track on mobile</p>
                {qrCodeUrl ? (
                  <img
                    src={qrCodeUrl}
                    alt='QR Code'
                    className='img-fluid'
                    style={{ maxWidth: '200px' }}
                  />
                ) : (
                  <div
                    className='d-flex justify-content-center align-items-center bg-light'
                    style={{ height: '200px', width: '200px' }}
                  >
                    <Spinner animation='border' />
                  </div>
                )}
              </div>
              <Button
                variant='outline-primary'
                onClick={handleCopyToClipboard}
                className='w-100'
              >
                <FaLink className='me-2' /> Copy Link
              </Button>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowShareModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminLayout;
